import React, { useRef, useState } from 'react';
import { sendPasswordResetEmail, signInWithEmailAndPassword } from 'firebase/auth';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { inspectionAuth } from '../services/inspectionFirebase';
import { LogoEmblem, LogoWordmark } from '../components/LogoEmblem';
import './InspectionSignIn.css';
import { FLEET_PRIVACY_URL, FLEET_SUPPORT_URL } from '../components/AccountPrivacyActions';

export default function InspectionSignIn({ onSignIn, accountDeletionStatus = '' }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [resetting, setResetting] = useState(false);
  const submittingRef = useRef(false);
  const emailInput = useRef(null);

  const submit = async (event) => {
    event.preventDefault();
    if (submittingRef.current || !event.currentTarget.reportValidity()) return;
    submittingRef.current = true;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const credential = await signInWithEmailAndPassword(inspectionAuth, email.trim(), password);
      onSignIn(credential.user);
    } catch (signInError) {
      console.error('Fleet sign-in failed:', signInError);
      setError(signInError.code === 'auth/network-request-failed'
        ? 'We could not connect. Check your internet connection and try again.'
        : 'We could not sign you in. Check your email and password, then try again.');
    } finally {
      submittingRef.current = false;
      setBusy(false);
    }
  };

  const sendResetEmail = async () => {
    setError('');
    setMessage('');
    if (!email.trim()) {
      setError('Enter your email address first to request a password reset.');
      emailInput.current?.focus();
      return;
    }

    setResetting(true);
    try {
      await sendPasswordResetEmail(inspectionAuth, email.trim());
      setMessage('If an account uses this email address, a password reset email has been sent.');
    } catch (resetError) {
      console.error('Fleet password reset request failed:', resetError);
      setError(resetError.code === 'auth/network-request-failed'
        ? 'We could not connect. Check your internet connection and try again.'
        : 'We could not send a password reset email. Check the email address and try again.');
    } finally {
      setResetting(false);
    }
  };

  return (
    <Box component="main" className="fleet-signin-screen">
      <Paper component="section" aria-labelledby="fleet-signin-heading" elevation={0} className="fleet-signin-card">
        <Stack spacing={3}>
          <Stack className="fleet-signin-brand-lockup" direction="row" spacing={2} alignItems="center">
            <LogoEmblem size={72} />
            <Box className="fleet-signin-brand">
              <LogoWordmark width={144} />
              <Box component="span">Fleet Management</Box>
            </Box>
          </Stack>

          {error && <Alert severity="error" role="alert">{error}</Alert>}
          {message && <Alert severity="success" role="status">{message}</Alert>}
          {accountDeletionStatus && <Alert severity={accountDeletionStatus === 'deleted' ? 'success' : 'info'} role="status">
            {accountDeletionStatus === 'deleted' ? 'Your account has been permanently deleted.'
              : 'Account deletion is in progress. Access has been blocked and cleanup will finish automatically.'}
          </Alert>}

          <Box component="form" onSubmit={submit} noValidate={false}>
            <Typography component="h1" variant="h4" id="fleet-signin-heading" className="fleet-signin-title">
              Sign in
            </Typography>
            <Stack spacing={2.25}>
              <TextField
                inputRef={emailInput}
                id="fleet-email"
                name="email"
                type="email"
                label="Work email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="username"
                inputProps={{ inputMode: 'email', 'aria-label': 'Work email' }}
                required
                fullWidth
                size="small"
                disabled={busy || resetting}
              />
              <TextField
                id="fleet-password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                label="Password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                required
                fullWidth
                size="small"
                inputProps={{ 'aria-label': 'Password' }}
                disabled={busy || resetting}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        aria-pressed={showPassword}
                        onClick={() => setShowPassword((visible) => !visible)}
                        edge="end"
                        disabled={busy || resetting}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <Box className="fleet-signin-reset-row">
                <Button
                  type="button"
                  variant="text"
                  onClick={sendResetEmail}
                  disabled={busy || resetting}
                  sx={{ textTransform: 'none', px: 0.5 }}
                >
                  {resetting ? 'Sending reset email…' : 'Forgot password?'}
                </Button>
              </Box>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={busy || resetting}
                startIcon={busy ? <CircularProgress size={18} color="inherit" /> : null}
                sx={{ minHeight: 50, borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
              >
                {busy ? 'Signing in…' : 'Sign in'}
              </Button>
            </Stack>
          </Box>

          <Typography variant="body2" className="fleet-signin-access-note">
            Fleet accounts are provided by an authorized administrator; sign-up is not available in this app. Contact your administrator if you need access or sign-in help.
          </Typography>
          <Typography variant="body2">
            <a href={FLEET_PRIVACY_URL} target="_blank" rel="noreferrer">Privacy policy</a>
            {' | '}<a href={FLEET_SUPPORT_URL} target="_blank" rel="noreferrer">Support</a>
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
}
